<template>
  <div class="container">
    <CopyTrackWrapper
      type="strategy"
      :title="copyTrackerParams.title"
      :uuid="copyTrackerParams.uuid"
      :demo="copyTrackerParams.demo">
      <PremiumRequired v-if="showPremiumBlock" />
      <template v-else>
        <StrategyBreadcrumbs v-if="breadcrumbsProps" v-bind="breadcrumbsProps" />
        <CopyTrackWrapper
          type="strategy_banner"
          :title="copyTrackerParams.title"
          :uuid="copyTrackerParams.uuid"
          :demo="copyTrackerParams.demo">
          <StrategyBanner v-if="bannerProps" v-bind="bannerProps" />
        </CopyTrackWrapper>
        <div class="row">
          <div class="col-sm-8">
            <StrategyResourceList v-if="resourcesProps" v-bind="resourcesProps" />
            <StrategyPanels v-if="implementationProps" v-bind="implementationProps" />
            <StrategyPanels v-if="examplesProps" v-bind="examplesProps" />
            <ResearchCitationPanels v-if="researchCitationProps" :research-citations="researchCitationProps" />
          </div>
          <div class="col-sm-4">
            <StrategySaveToLibraryPanel
              v-if="libraryActionsProps"
              v-bind="libraryActionsProps" />
            <StrategyCollectionBreadcrumb :collections="collections"/>
          </div>
        </div>
        <StrategyRelated v-if="relatedProps" v-bind="relatedProps" />
        <SubmitFeedback v-if="feedbackProps" v-bind="feedbackProps" />
      </template>
    </CopyTrackWrapper>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'

import { getStrategy } from '@/api/strategies'
import { getLibraryGoal } from '@/api/library.js'
import { getAllCuratedCollections } from '@/api/pathways.js'
import { setTitle } from '@/util/title'

import { strategyPageMixin } from '@/components/strategies/strategy_mixins.js'

import StrategyBreadcrumbs from '@/components/strategies/details/StrategyBreadcrumbs'
import StrategySaveToLibraryPanel from '@/components/strategies/details/StrategySaveToLibraryPanel'
import StrategyCollectionBreadcrumb from './StrategyCollectionBreadcrumb'
import PremiumRequired from '@/components/PremiumRequired.vue'

export default {
  name: 'StrategyDetailPage',
  mixins: [strategyPageMixin],
  components: {
    PremiumRequired,
    StrategyBreadcrumbs,
    StrategySaveToLibraryPanel,
    StrategyCollectionBreadcrumb
  },
  props: {
    slugOrUuid: {
      type: String,
      required: true
    },
    libraryGoalUuid: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      copyTrackerParams: {},
      breadcrumbsProps: null,
      libraryActionsProps: null,
      feedbackProps: null,

      strategyData: {},
      collections: []
    }
  },
  computed: {
    ...mapState('user', {
      userUuid: state => state.user.uuid,
    }),
    ...mapGetters({
      isPremiumUser: 'user/isPremium'
    }),
    showPremiumBlock() {
      return !!(
        !this.isPremiumUser
        && !this.strategyData.demo
        && this.strategyData.uuid // prevent showing block before strategy loads
      )
    }
  },
  watch: {
    $route(to, from) {
      this.fetchContent()
    }
  },
  created() {
    this.fetchContent()
  },
  methods: {
    fetchContent() {
      let promises = []
      promises.push(getStrategy(this.slugOrUuid))
      if (this.libraryGoalUuid) {
        promises.push(
          getLibraryGoal(this.libraryGoalUuid).catch(() => {
            return false
          })
        )
      }

      Promise.all(promises).then((data)=>{
        this.parseStrategy(data)
        this.$sendEvent(
          'Viewed Strategy',
          {
            uuid: this.strategyData.uuid,
            title: this.strategyData.title,
            udl_checkpoints: this.strategyData.udl_standards_refs
          },
          {
            cu: this.strategyData.uuid,
            ct: this.strategyData.title
          }
        )
      })
    },
    async parseStrategy([strategyData, libraryGoalData]) {
      if (strategyData.status === 'error') {
        console.error(strategyData.message)
        return
      }
      this.strategyData = strategyData

      setTitle(`${strategyData.title} | UDL Strategies`)

      this.setCommonProps(strategyData, libraryGoalData ? true : false)

      this.copyTrackerParams = {
        uuid: strategyData.uuid,
        title: strategyData.title,
        demo: strategyData.demo
      }

      this.breadcrumbsProps = {
        title: strategyData.title,
        tags: strategyData.tags
      }

      this.libraryActionsProps = {
        strategyUuid: strategyData.uuid
      }

      this.feedbackProps = {
        type: 'Strategy Detail Page',
        uuid: strategyData.uuid,
        title: strategyData.title
      }
      try {
        var data = await getAllCuratedCollections({ content: 'true' })
        data.collections.forEach(collection => {
          if (
            collection.content.some(content => {
              return content.content_uuid === strategyData.uuid
            })
          ) {
            this.collections = this.collections.concat([
              {
                uuid: collection.uuid,
                name: collection.name
              }
            ])
          }
        })
      } catch (error) {
        console.log(error)
      }
    }
  },
  eventContext() {
    return {
      strategy_uuid: this.strategyData.uuid,
      strategy_title: this.strategyData.title,
      research_citations_count: this.strategyData.research_citations
        ? this.strategyData.research_citations.length
        : 0
    }
  }
}
</script>

<style lang='less' scoped>
.library-panel {
  margin-top: 48px;
}
</style>
